/**
 * AI Gift Finder API constants.
 *
 * The contract documents paths as `/search/{apiVersion}/gift-finder/*`. This repo has no
 * generic apiVersion substitution (versions are literal in URL strings, see plpApi/constants.js),
 * so the token is resolved from a single env-backed constant here.
 */

/** Resolved `{apiVersion}` path token. */
export const GIFT_FINDER_API_VERSION = process.env.NEXT_PUBLIC_GIFT_FINDER_API_VERSION || "v1";

/** Base path shared by all three endpoints. */
const giftFinderBasePath = () => `/search/${GIFT_FINDER_API_VERSION}/gift-finder`;

/**
 * Launch-rail prompts endpoint.
 *
 * `geoId` scopes the prompts to the catalog the user is browsing, so an international page
 * offers prompts for that country rather than domestic ones.
 *
 * @param {string} [geoId] - Catalog id; omitted from the URL when absent.
 * @returns {string}
 */
export const GET_GIFT_FINDER_PROMPTS_URL = (geoId) =>
  `${giftFinderBasePath()}/prompts${geoId ? `?geo=${encodeURIComponent(geoId)}` : ""}`;

/** @returns {string} Chip/question config endpoint. */
export const GET_GIFT_FINDER_QUESTIONS_URL = () => `${giftFinderBasePath()}/questions`;

/** @returns {string} Answer submission / results endpoint. */
export const POST_GIFT_FINDER_SEARCH_URL = () => `${giftFinderBasePath()}/search`;

/**
 * Error codes. The first three come from the contract; NETWORK_ERROR is synthesised
 * client-side for timeouts and non-responses, which never reach the server envelope.
 */
export const GIFT_FINDER_ERROR_CODES = {
  VALIDATION_FAILED: "VALIDATION_FAILED",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
  NETWORK_ERROR: "NETWORK_ERROR",
};

/** User-facing copy per error code. */
export const GIFT_FINDER_ERROR_COPY = {
  [GIFT_FINDER_ERROR_CODES.VALIDATION_FAILED]: "Something in your selection isn't supported. Try changing a filter.",
  [GIFT_FINDER_ERROR_CODES.INTERNAL_ERROR]: "Something went wrong on our side. Please try again.",
  [GIFT_FINDER_ERROR_CODES.SERVICE_UNAVAILABLE]: "We're having trouble reaching our gift experts. Please retry.",
  [GIFT_FINDER_ERROR_CODES.NETWORK_ERROR]: "We're having trouble reaching our gift experts. Please retry.",
};

/**
 * Page size for the search endpoint. The contract flags the backend default as unconfirmed,
 * so this stays a single named constant rather than being inlined at call sites.
 */
export const GIFT_FINDER_PAGE_SIZE = {
  MOBILE: 20,
  DESKTOP: 40,
};

/**
 * Session cache key for the /questions payload. Bump the suffix whenever the adapter's
 * expected shape changes so stale cached payloads self-invalidate.
 */
export const GIFT_FINDER_QUESTIONS_CACHE_KEY = "gf_questions_v3";

/** Session key holding the last submitted answers ledger (label/media hydration hint only). */
export const GIFT_FINDER_LAST_ANSWERS_KEY = "gf_last_answers";

/**
 * Fixed step order from the contract. FE walks this order and presents the first
 * step whose answer is still unset.
 */
export const GIFT_FINDER_STEPS = {
  RECIPIENT: "recipient",
  OCCASION: "occasion",
  BUDGET: "budget",
  WHEN: "when",
  WHERE: "where",
  INTERESTS: "interests",
};

/**
 * Value stored for a `where` answer with no delivery area — the user confirmed a country
 * without a pincode or city, so the search runs across that whole catalog.
 */
export const ANY_LOCATION_VALUE = "any";

/** Route the quiz hands off to. */
export const GIFT_FINDER_RESULTS_ROUTE = "/gift-finder";
